import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';

import { EnvironmentUrlService } from '../../core/services/environment-url.service';
import {
  AgentCompaction,
  AgentMessagePage,
  AgentRun,
  AgentSession,
  AgentSessionChanges,
  AgentSessionDetails,
  AgentSpend,
  AgentTodo,
  ClassicBook,
  DeskFeatures,
  ExportPlan,
  FileClaim,
  HooksSetting,
  HousekeepingView,
  MatterPlacement,
  MessageSent,
  NewAgentSession,
  NodeInfo,
  NodeKind,
  NodeView,
  OpenSuggestion,
  ProposalDecision,
  ProposalView,
  RejectedIdea,
  RevisionContent,
  RevisionInfo,
  RewriteRequest,
  RewriteResult,
  SaveResult,
  SendMessage,
  SpendingGuardrail,
  TemplateView,
  TextChange,
  TrashItem,
  UndoResult,
  UploadOutcome,
  WalletBalance,
  WorkflowView,
  WorkspaceDetails,
  WorkspaceSummary,
} from './desk-api.models';
import { ArcEntry, BeatRequest, CalendarRequest, EventRequest, EventSaved, PageMatch, StoryItemSaved, StoryOverview, ThreadRequest, ThreadStatus, ThreadsView, TimelineView } from './story.models';

/** The Desk REST routes (TD §16). The project stream is read by `DeskEventsService`. */
@Injectable({ providedIn: 'root' })
export class DeskApiService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(EnvironmentUrlService).apiUrl;

  private get base(): string {
    return `${this.api}/workspaces`;
  }

  // Projects

  listProjects(): Observable<WorkspaceSummary[]> {
    return this.http.get<WorkspaceSummary[]>(this.base);
  }

  createProject(title: string, template: string): Observable<WorkspaceDetails> {
    return this.http.post<WorkspaceDetails>(this.base, { title, template });
  }

  getProject(id: string): Observable<WorkspaceDetails> {
    return this.http.get<WorkspaceDetails>(`${this.base}/${id}`);
  }

  /** Moves a book to the trash: it leaves the writer's books, and every route answers 404. */
  deleteProject(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  renameProject(id: string, title: string): Observable<WorkspaceDetails> {
    return this.http.patch<WorkspaceDetails>(`${this.base}/${id}`, { title });
  }

  /** The spending guardrail: when a task pauses and asks (TD §13.4). */
  setPauseAt(id: string, pauseAt: SpendingGuardrail): Observable<WorkspaceDetails> {
    return this.http.patch<WorkspaceDetails>(`${this.base}/${id}`, { pauseAt });
  }

  /** Whether the writer gets an email when an agent has waited on them for 10 minutes (D2.04). */
  setEmailWhenWaiting(id: string, emailWhenWaiting: boolean): Observable<WorkspaceDetails> {
    return this.http.patch<WorkspaceDetails>(`${this.base}/${id}`, { emailWhenWaiting });
  }

  /** Switches hooks on or off (D3.07); one left out stays as it is. */
  setHooks(id: string, hooks: Partial<HooksSetting>): Observable<WorkspaceDetails> {
    return this.http.patch<WorkspaceDetails>(`${this.base}/${id}`, { hooks });
  }

  /** What housekeeping did and costs in the book, and what waits to be confirmed (D3.08). */
  housekeeping(id: string): Observable<HousekeepingView> {
    return this.http.get<HousekeepingView>(`${this.base}/${id}/housekeeping`);
  }

  templates(): Observable<TemplateView[]> {
    return this.http.get<TemplateView[]>(`${this.base}/templates`);
  }

  workflows(): Observable<WorkflowView[]> {
    return this.http.get<WorkflowView[]>(`${this.base}/workflows`);
  }

  // Tree and nodes

  tree(id: string): Observable<NodeInfo[]> {
    return this.http.get<NodeInfo[]>(`${this.base}/${id}/tree`);
  }

  getNode(id: string, nodeId: string): Observable<NodeView> {
    return this.http.get<NodeView>(`${this.base}/${id}/nodes/${nodeId}`);
  }

  /**
   * `parentId` null creates it at the top of the Notebook. `placement` puts a front or back matter page directly in the
   * Manuscript before or after the story; left out, its name decides.
   */
  createNode(id: string, parentId: string | null, name: string, kind: NodeKind, content?: string, placement?: MatterPlacement): Observable<NodeInfo> {
    return this.http.post<NodeInfo>(`${this.base}/${id}/nodes`, { parentId, name, kind, content, placement });
  }

  /** Puts the Manuscript's parts in this order (every part once); the chapters are renumbered to follow. */
  reorderParts(id: string, partIds: readonly string[]): Observable<NodeInfo[]> {
    return this.http.put<NodeInfo[]>(`${this.base}/${id}/parts/order`, { partIds });
  }

  renameNode(id: string, nodeId: string, name: string): Observable<NodeInfo> {
    return this.http.patch<NodeInfo>(`${this.base}/${id}/nodes/${nodeId}`, { name });
  }

  /** `parentId` null moves it to the top of the Notebook. */
  moveNode(id: string, nodeId: string, parentId: string | null): Observable<NodeInfo> {
    const body = parentId ? { parentId } : { toNotebookRoot: true };
    return this.http.patch<NodeInfo>(`${this.base}/${id}/nodes/${nodeId}`, body);
  }

  // Uploads and files (D2.17)

  /**
   * One file into a Notebook folder, the Inbox by default, with upload progress. The key makes a retry after a
   * dropped connection land once.
   */
  upload(id: string, file: File, key: string, folderId?: string | null): Observable<HttpEvent<{ files: UploadOutcome[] }>> {
    const form = new FormData();
    form.append('files', file, file.name);
    form.append('keys', key);
    if (folderId) {
      form.append('folderId', folderId);
    }

    return this.http.post<{ files: UploadOutcome[] }>(`${this.base}/${id}/uploads`, form, { reportProgress: true, observe: 'events' });
  }

  /** A stored file by its hash. It never changes, so the browser keeps it. */
  blob(id: string, sha: string): Observable<Blob> {
    return this.http.get(`${this.base}/${id}/blobs/${sha}`, { responseType: 'blob' });
  }

  // Export (D3.15)

  exportPlan(id: string): Observable<ExportPlan> {
    return this.http.get<ExportPlan>(`${this.base}/${id}/export`);
  }

  /** Sets the name and cover the book is exported with; an empty name goes back to the writer's own. */
  setExport(id: string, settings: { author: string; cover: string | null }): Observable<ExportPlan> {
    return this.http.put<ExportPlan>(`${this.base}/${id}/export`, settings);
  }

  /** The Manuscript as an EPUB. */
  exportEpub(id: string): Observable<Blob> {
    return this.http.get(`${this.base}/${id}/export/epub`, { responseType: 'blob' });
  }

  // Classic (D2.22)

  classicBooks(): Observable<ClassicBook[]> {
    return this.http.get<ClassicBook[]>(`${this.base}/import-classic`);
  }

  importClassic(bookProjectId: string): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`${this.base}/import-classic/${bookProjectId}`, {});
  }

  /** Moves a node to the trash, with everything under it. */
  deleteNode(id: string, nodeId: string): Observable<NodeInfo> {
    return this.http.delete<NodeInfo>(`${this.base}/${id}/nodes/${nodeId}`);
  }

  trash(id: string): Observable<TrashItem[]> {
    return this.http.get<TrashItem[]>(`${this.base}/${id}/trash`);
  }

  restore(id: string, nodeId: string): Observable<NodeInfo> {
    return this.http.post<NodeInfo>(`${this.base}/${id}/trash/${nodeId}/restore`, {});
  }

  // Story state (D3.01–D3.13)

  /** Open conflicts and quiet threads: what Home and the sidebar point at. */
  storyOverview(id: string): Observable<StoryOverview> {
    return this.http.get<StoryOverview>(`${this.base}/${id}/story`);
  }

  timeline(id: string): Observable<TimelineView> {
    return this.http.get<TimelineView>(`${this.base}/${id}/timeline`);
  }

  /** The book's calendar: ours, Julian, days from the start, or its own months. */
  setCalendar(id: string, calendar: CalendarRequest): Observable<void> {
    return this.http.put<void>(`${this.base}/${id}/timeline/calendar`, calendar);
  }

  /** The writer's own event, or a change to any event, which makes it theirs. */
  saveEvent(id: string, event: EventRequest, eventId?: string): Observable<EventSaved> {
    return eventId
      ? this.http.put<EventSaved>(`${this.base}/${id}/timeline/events/${encodeURIComponent(eventId)}`, event)
      : this.http.post<EventSaved>(`${this.base}/${id}/timeline/events`, event);
  }

  /** Confirms the agents' events with these ids, or all of them. */
  confirmEvents(id: string, ids?: string[]): Observable<{ confirmed: number }> {
    return this.http.post<{ confirmed: number }>(`${this.base}/${id}/timeline/confirm`, { ids: ids ?? null });
  }

  removeEvent(id: string, eventId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/timeline/events/${encodeURIComponent(eventId)}`);
  }

  /** "It's fine", or back to open: remembered when the checks run again (D3.02). */
  setConflictFine(id: string, conflictId: string, fine: boolean): Observable<void> {
    const url = `${this.base}/${id}/timeline/conflicts/${encodeURIComponent(conflictId)}/fine`;
    return fine ? this.http.put<void>(url, {}) : this.http.delete<void>(url);
  }

  threads(id: string): Observable<ThreadsView> {
    return this.http.get<ThreadsView>(`${this.base}/${id}/threads`);
  }

  /** Confirms threads and beats by id, or everything agents added; a thread's id confirms its beats too. */
  confirmThreads(id: string, ids?: string[]): Observable<{ confirmed: number }> {
    return this.http.post<{ confirmed: number }>(`${this.base}/${id}/threads/confirm`, { ids: ids ?? null });
  }

  /** The writer's own thread, or a change to any thread's fields, which makes it theirs. */
  saveThread(id: string, thread: ThreadRequest, threadId?: string): Observable<StoryItemSaved> {
    return threadId
      ? this.http.put<StoryItemSaved>(`${this.base}/${id}/threads/${encodeURIComponent(threadId)}`, thread)
      : this.http.post<StoryItemSaved>(`${this.base}/${id}/threads`, thread);
  }

  /** The writer's own beat on a thread, or a change to any beat, which makes it theirs. */
  saveBeat(id: string, threadId: string, beat: BeatRequest, beatId?: string): Observable<StoryItemSaved> {
    const beats = `${this.base}/${id}/threads/${encodeURIComponent(threadId)}/beats`;
    return beatId ? this.http.put<StoryItemSaved>(`${beats}/${encodeURIComponent(beatId)}`, beat) : this.http.post<StoryItemSaved>(beats, beat);
  }

  /** Mark resolved, or Reopen. */
  setThreadStatus(id: string, threadId: string, status: ThreadStatus): Observable<void> {
    return this.http.put<void>(`${this.base}/${id}/threads/${encodeURIComponent(threadId)}/status`, { status });
  }

  /** Removes a thread or a beat. */
  removeThreadItem(id: string, itemId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/threads/items/${encodeURIComponent(itemId)}`);
  }

  /** Confirms these entries of a character's arc, or all the agents' entries. */
  confirmArc(id: string, nodeId: string, entries?: Pick<ArcEntry, 'ch' | 'kind' | 'text'>[]): Observable<{ confirmed: number }> {
    return this.http.post<{ confirmed: number }>(`${this.base}/${id}/characters/${nodeId}/arc/confirm`, { entries: entries ?? null });
  }

  removeArcEntry(id: string, nodeId: string, entry: Pick<ArcEntry, 'ch' | 'kind' | 'text'>): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/characters/${nodeId}/arc/remove`, entry);
  }

  /** The writer's own line in a character's arc (what `arc_add` records): needs no confirming. 409 `duplicate` when the arc has it. */
  addArcEntry(id: string, nodeId: string, entry: Pick<ArcEntry, 'ch' | 'kind' | 'text' | 'planned'>): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/characters/${nodeId}/arc`, entry);
  }

  /** Rewrites a line of a character's arc; an agent's line becomes the writer's, and confirmed. */
  changeArcEntry(id: string, nodeId: string, from: Pick<ArcEntry, 'ch' | 'kind' | 'text'>, to: Pick<ArcEntry, 'ch' | 'kind' | 'text' | 'planned'>): Observable<void> {
    return this.http.put<void>(`${this.base}/${id}/characters/${nodeId}/arc`, { from, to });
  }

  /** A note on a day of the timeline, by its sort key (`1582-02-27`, or a day number); an empty note removes it. */
  setDayNote(id: string, day: string, note: string): Observable<void> {
    return this.http.put<void>(`${this.base}/${id}/timeline/day-notes`, { day, note });
  }

  removeDayNote(id: string, day: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/timeline/day-notes/${encodeURIComponent(day)}`);
  }

  /** Pages by name or alias, best first: what `@` in the editor offers (D3.05). */
  lookup(id: string, q: string, limit = 8): Observable<PageMatch[]> {
    return this.http.get<PageMatch[]>(`${this.base}/${id}/lookup`, { params: new HttpParams().set('q', q).set('limit', limit) });
  }

  // Writing (D1.40–D1.44)

  /** Saves from the editor. If an agent changed the file since `baseRevision`, the server merges per paragraph. */
  saveContent(id: string, nodeId: string, baseRevision: number | null, content: string): Observable<SaveResult> {
    return this.http.put<SaveResult>(`${this.base}/${id}/nodes/${nodeId}/content`, { baseRevision, content });
  }

  /** The editor has unsaved edits in the file (renewed while they last): agent writes to it become suggestions. */
  setEditing(id: string, nodeId: string, editing: boolean): Observable<void> {
    return this.http.put<void>(`${this.base}/${id}/nodes/${nodeId}/editing`, { editing });
  }

  proposal(id: string, nodeId: string, number: number): Observable<ProposalView> {
    return this.http.get<ProposalView>(`${this.base}/${id}/nodes/${nodeId}/proposals/${number}`);
  }

  /** The whole proposal without `changes`, or the chosen changes against `againstRevision`. */
  acceptProposal(id: string, nodeId: string, number: number, part?: { againstRevision: number; changes: number[] }): Observable<ProposalDecision> {
    return this.http.post<ProposalDecision>(`${this.base}/${id}/nodes/${nodeId}/proposals/${number}/accept`, part ?? {});
  }

  rejectProposal(id: string, nodeId: string, number: number, part?: { againstRevision: number; changes: number[] }): Observable<ProposalDecision> {
    return this.http.post<ProposalDecision>(`${this.base}/${id}/nodes/${nodeId}/proposals/${number}/reject`, part ?? {});
  }

  /** Inline rewrite (D1.46): one model call for the selection, written as one proposal holding every version. */
  rewrite(id: string, nodeId: string, request: RewriteRequest): Observable<RewriteResult> {
    return this.http.post<RewriteResult>(`${this.base}/${id}/nodes/${nodeId}/rewrite`, request);
  }

  /** Puts another of a rewrite's versions in its proposal, without another model call. */
  rewriteVersion(id: string, nodeId: string, number: number, version: number): Observable<ProposalView> {
    return this.http.post<ProposalView>(`${this.base}/${id}/nodes/${nodeId}/proposals/${number}/version`, { version });
  }

  /** Newest first, open proposals included. */
  revisions(id: string, nodeId: string): Observable<RevisionInfo[]> {
    return this.http.get<RevisionInfo[]>(`${this.base}/${id}/nodes/${nodeId}/revisions`);
  }

  revision(id: string, nodeId: string, number: number): Observable<RevisionContent> {
    return this.http.get<RevisionContent>(`${this.base}/${id}/nodes/${nodeId}/revisions/${number}`);
  }

  /** What changed from revision `from` to revision `to`. */
  diff(id: string, nodeId: string, from: number, to: number): Observable<TextChange[]> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<TextChange[]>(`${this.base}/${id}/nodes/${nodeId}/revisions/diff`, { params });
  }

  /** Makes an old revision's text current again, as a new revision. */
  restoreRevision(id: string, nodeId: string, number: number): Observable<RevisionInfo> {
    return this.http.post<RevisionInfo>(`${this.base}/${id}/nodes/${nodeId}/revisions/${number}/restore`, {});
  }

  resolveComment(id: string, nodeId: string, commentId: string, resolved: boolean): Observable<void> {
    return this.http.patch<void>(`${this.base}/${id}/nodes/${nodeId}/comments/${commentId}`, { resolved });
  }

  /** A writer note on a passage, for the writer or for an agent (D2.15). */
  addNote(id: string, nodeId: string, note: { quote: string; body: string; forAgent: boolean }): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`${this.base}/${id}/nodes/${nodeId}/comments`, note);
  }

  /** Switches a note between "for me" and "for an agent". */
  setNoteForAgent(id: string, nodeId: string, commentId: string, forAgent: boolean): Observable<void> {
    return this.http.patch<void>(`${this.base}/${id}/nodes/${nodeId}/comments/${commentId}`, { forAgent });
  }

  /** Rewords one of the writer's own notes (409 `not_allowed` on an agent's comment). */
  editNote(id: string, nodeId: string, commentId: string, body: string): Observable<void> {
    return this.http.patch<void>(`${this.base}/${id}/nodes/${nodeId}/comments/${commentId}`, { body });
  }

  /**
   * The writer's reply at the end of a note's thread (D3.25). `toldAgent`: the note's agent was at work and reads it
   * before its next step.
   */
  replyToNote(id: string, nodeId: string, commentId: string, body: string): Observable<{ id: string; toldAgent: boolean }> {
    return this.http.post<{ id: string; toldAgent: boolean }>(`${this.base}/${id}/nodes/${nodeId}/comments/${commentId}/replies`, { body });
  }

  /** Rewords one of the writer's own replies. */
  editNoteReply(id: string, nodeId: string, commentId: string, replyId: string, body: string): Observable<void> {
    return this.http.patch<void>(`${this.base}/${id}/nodes/${nodeId}/comments/${commentId}/replies/${replyId}`, { body });
  }

  /** Deletes a reply for good, the writer's or an agent's. */
  deleteNoteReply(id: string, nodeId: string, commentId: string, replyId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/nodes/${nodeId}/comments/${commentId}/replies/${replyId}`);
  }

  /** Deletes a note or an agent's comment for good, with its thread. */
  deleteComment(id: string, nodeId: string, commentId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/nodes/${nodeId}/comments/${commentId}`);
  }

  // Ideas set aside (D3.22)

  rejectedIdeas(id: string): Observable<RejectedIdea[]> {
    return this.http.get<RejectedIdea[]>(`${this.base}/${id}/rejected-ideas`);
  }

  /**
   * Sets an idea aside. With `askAgent`, the agent whose suggestion it was is asked in its conversation where else the
   * book still uses it (`askedAgent` says whether it was).
   */
  setIdeaAside(
    id: string,
    idea: { text: string; passage?: string | null; nodeId?: string | null; sessionId?: string | null; askAgent?: boolean },
  ): Observable<{ idea: RejectedIdea; askedAgent: boolean }> {
    return this.http.post<{ idea: RejectedIdea; askedAgent: boolean }>(`${this.base}/${id}/rejected-ideas`, idea);
  }

  rewordRejectedIdea(id: string, ideaId: string, text: string): Observable<void> {
    return this.http.patch<void>(`${this.base}/${id}/rejected-ideas/${ideaId}`, { text });
  }

  /** Agents may use the idea again. */
  removeRejectedIdea(id: string, ideaId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/rejected-ideas/${ideaId}`);
  }

  /**
   * Splits a Notebook page into pages or chapters of their own by line ranges, all at once (D3.25). The original goes
   * to Research/Sources/Originals. 422 `invalid_split` says what doesn't fit.
   */
  splitPage(id: string, nodeId: string, parts: { firstLine: number; lastLine: number; to: string; chapter?: number | null }[]): Observable<{ done: string[] }> {
    return this.http.post<{ done: string[] }>(`${this.base}/${id}/nodes/${nodeId}/split`, { parts });
  }

  /** Every open suggested change in the project, for Home and the sidebar (D2.12). */
  suggestions(id: string): Observable<OpenSuggestion[]> {
    return this.http.get<OpenSuggestion[]>(`${this.base}/${id}/suggestions`);
  }

  /** Undo this run: every page it touched goes back to how it was before, unless someone changed it since. */
  undoRun(id: string, runId: string): Observable<UndoResult> {
    return this.http.post<UndoResult>(`${this.base}/${id}/runs/${runId}/undo`, {});
  }

  // Agents

  sessions(id: string, archived = false): Observable<AgentSession[]> {
    return this.http.get<AgentSession[]>(`${this.base}/${id}/sessions`, { params: new HttpParams().set('archived', archived) });
  }

  session(id: string, sessionId: string): Observable<AgentSessionDetails> {
    return this.http.get<AgentSessionDetails>(`${this.base}/${id}/sessions/${sessionId}`);
  }

  createSession(id: string, settings: NewAgentSession): Observable<AgentSession> {
    return this.http.post<AgentSession>(`${this.base}/${id}/sessions`, settings);
  }

  /** Title, model, autonomy, effort, archive. Changes apply from the agent's next call. */
  updateSession(id: string, sessionId: string, changes: AgentSessionChanges): Observable<AgentSession> {
    return this.http.patch<AgentSession>(`${this.base}/${id}/sessions/${sessionId}`, changes);
  }

  /** The transcript, oldest first, a page at a time from the newest. */
  messages(id: string, sessionId: string, before?: number, limit = 50): Observable<AgentMessagePage> {
    let params = new HttpParams().set('limit', limit);
    if (before != null) {
      params = params.set('before', before);
    }
    return this.http.get<AgentMessagePage>(`${this.base}/${id}/sessions/${sessionId}/messages`, { params });
  }

  /**
   * Sends the writer's message and starts a run; its progress comes over the project stream. While the agent
   * works, the message is queued for its next step (`queued`, D2.03).
   */
  send(id: string, sessionId: string, message: SendMessage): Observable<MessageSent> {
    return this.http.post<MessageSent>(`${this.base}/${id}/sessions/${sessionId}/messages`, message);
  }

  /** The writer's version of an agent's plan (D3.25): the whole list, which the agent follows from its next step. */
  setTodos(id: string, sessionId: string, todos: AgentTodo[]): Observable<AgentTodo[]> {
    return this.http.put<AgentTodo[]>(`${this.base}/${id}/sessions/${sessionId}/todos`, { todos });
  }

  compact(id: string, sessionId: string): Observable<AgentCompaction> {
    return this.http.post<AgentCompaction>(`${this.base}/${id}/sessions/${sessionId}/compact`, {});
  }

  /** A new conversation with the same settings, seeded with a summary of this one. */
  startFresh(id: string, sessionId: string): Observable<AgentSession> {
    return this.http.post<AgentSession>(`${this.base}/${id}/sessions/${sessionId}/fresh`, {});
  }

  run(id: string, runId: string): Observable<AgentRun> {
    return this.http.get<AgentRun>(`${this.base}/${id}/runs/${runId}`);
  }

  /** Stop ends the run after its current step; `now` also cancels the call in flight. */
  stopRun(id: string, runId: string, now = false): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/runs/${runId}/stop`, {}, { params: new HttpParams().set('now', now) });
  }

  /** The run keeps going without its pane; the desk says when it needs the writer or finishes (D2.04). */
  sendToBackground(id: string, runId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/runs/${runId}/background`, {});
  }

  /** The files agents have claimed while they work on them (D2.02). */
  claims(id: string): Observable<FileClaim[]> {
    return this.http.get<FileClaim[]>(`${this.base}/${id}/claims`);
  }

  answer(id: string, runId: string, answer: string): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/runs/${runId}/answer`, { answer });
  }

  approve(id: string, runId: string, decision: 'approve' | 'approve_for_run' | 'deny', note?: string): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/runs/${runId}/approve`, { decision, note });
  }

  /** Keep going past the step cap or the guardrail, or carry on after a top-up. */
  continueRun(id: string, runId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/runs/${runId}/continue`, {});
  }

  /** What agents cost since `since` (the writer's local midnight for "today"). */
  spend(id: string, since?: Date): Observable<AgentSpend> {
    const params = since ? new HttpParams().set('since', since.toISOString()) : undefined;
    return this.http.get<AgentSpend>(`${this.base}/${id}/spend`, { params });
  }

  // Outside api/workspaces

  wallet(): Observable<number> {
    return this.http.get<WalletBalance>(`${this.api}/wallet/balance`).pipe(map(w => w.totalCredits));
  }

  private features$?: Observable<DeskFeatures>;

  /** Whether the user has Desk. The one Desk route that isn't behind the flag; asked once per page load. */
  features(): Observable<DeskFeatures> {
    this.features$ ??= this.http.get<DeskFeatures>(`${this.api}/features`).pipe(
      // A failed check hides Desk for now and asks again next time.
      catchError(() => {
        this.features$ = undefined;
        return of({ desk: false });
      }),
      shareReplay(1),
    );
    return this.features$;
  }

  /** Try Desk: the writer turns Desk on for themselves while the beta is open (D3.16). */
  joinBeta(): Observable<DeskFeatures> {
    return this.http.post<DeskFeatures>(`${this.api}/features/desk/beta`, null).pipe(map(features => this.remember(features)));
  }

  /** Leaves the beta. Desk an administrator gave stays; the books stay either way. */
  leaveBeta(): Observable<DeskFeatures> {
    return this.http.delete<DeskFeatures>(`${this.api}/features/desk/beta`).pipe(map(features => this.remember(features)));
  }

  private remember(features: DeskFeatures): DeskFeatures {
    this.features$ = of(features).pipe(shareReplay(1));
    return features;
  }
}
